import { Button } from "./ui/button";
import { Phone, Star } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useState } from "react";

export const Hero = () => {
  const [shouldUseParallax, setShouldUseParallax] = useState(false);

  // Check for desktop and reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateParallax = () => {
      setShouldUseParallax(mediaQuery.matches && !reducedMotionQuery.matches);
    };
    updateParallax();
    mediaQuery.addEventListener("change", updateParallax);
    reducedMotionQuery.addEventListener("change", updateParallax);
    return () => {
      mediaQuery.removeEventListener("change", updateParallax);
      reducedMotionQuery.removeEventListener("change", updateParallax);
    };
  }, []);

  // Parallax scroll effect
  const { scrollY } = useScroll();
  const backgroundY = useTransform(scrollY, [0, 500], [0, 150]);

  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToReviews = () => {
    const element = document.getElementById("reviews");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" as const },
    },
  };

  return (
    <section className="relative min-h-[75vh] lg:min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Parallax Background — real <img> for LCP discoverability */}
      <motion.div
        className="absolute inset-0 w-full h-full"
        style={{ y: shouldUseParallax ? backgroundY : 0 }}
      >
        <img
          src="/images/hero-yacht.webp"
          alt=""
          fetchPriority="high"
          decoding="sync"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "70% 30%" }}
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 hero-gradient" />
      </motion.div>

      {/* Gradient styles for mobile and desktop */}
      <style>{`
        .hero-gradient {
          background: linear-gradient(
            90deg,
            rgba(5, 28, 44, 0.88) 0%,
            rgba(5, 28, 44, 0.80) 45%,
            rgba(5, 28, 44, 0.10) 100%
          );
        }

        @media (min-width: 1024px) {
          .hero-gradient {
            background: linear-gradient(
              90deg,
              rgba(5, 28, 44, 0.85) 0%,
              rgba(5, 28, 44, 0.70) 35%,
              rgba(5, 28, 44, 0.00) 100%
            ) !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      <div className="container mx-auto px-5 py-20 relative z-10">
        {/* Mobile: centered content */}
        <motion.div
          className="max-w-4xl mx-auto text-center lg:text-left lg:mx-0 lg:ml-[6vw] lg:mt-[20vh] lg:max-w-[680px]"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Eyebrow */}
          <motion.p
            className="text-xs md:text-sm font-semibold text-seafoam-300 mb-4 tracking-wide uppercase font-sans"
            variants={itemVariants}
          >
            Marine Windows & Hatches — San Diego
          </motion.p>

          {/* H1 */}
          <motion.h1
            className="font-serif font-bold text-white mb-6 leading-tight"
            style={{ fontSize: "clamp(30px, 8vw, 68px)" }}
            variants={itemVariants}
          >
            Boat Window & Hatch Specialists
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            className="text-base md:text-lg lg:text-xl mb-8 max-w-[32ch] lg:max-w-[580px] mx-auto lg:mx-0 leading-relaxed font-sans"
            style={{ color: "rgba(255, 255, 255, 0.9)" }}
            variants={itemVariants}
          >
            Precision fabrication, installation, and repair for glass and
            acrylic—serving San Diego County and Southern California marinas.
          </motion.p>

          {/* CTAs */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8"
            variants={itemVariants}
          >
            <Button
              size="lg"
              onClick={scrollToContact}
              className="bg-white hover:bg-white/90 text-navy-900 hover:text-navy-800 text-base lg:text-lg px-8 lg:px-10 py-3 lg:py-4 font-bold rounded-lg shadow-xl hover:shadow-2xl focus:ring-2 focus:ring-offset-2 focus:ring-white transition-all active:translate-y-[1px] w-full sm:w-auto"
            >
              Request a Quote
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="bg-transparent hover:bg-white/10 text-white hover:text-white border-2 border-white/80 hover:border-white text-base lg:text-lg px-6 lg:px-8 font-semibold rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-white transition-all active:translate-y-[1px] w-full sm:w-auto"
            >
              <a
                href="tel:6193774673"
                className="flex items-center justify-center w-full"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call (619) 377-4673
              </a>
            </Button>
          </motion.div>

          {/* Trust Row */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 text-sm font-sans"
            aria-label="Rated 5 out of 5 stars on Google"
            variants={itemVariants}
          >
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className="h-5 w-5 fill-gold text-gold"
                  aria-hidden="true"
                />
              ))}
            </div>
            <span className="text-seafoam-300 font-medium text-[15px]">
              Rated 5.0 on Google — 25+ client reviews
            </span>
            <button
              onClick={scrollToReviews}
              className="text-azure-500 hover:text-azure-hover font-semibold underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-azure-hover focus:ring-offset-2 rounded transition-colors"
              aria-label="Read customer reviews"
            >
              Read Reviews →
            </button>
          </motion.div>
        </motion.div>
      </div>

      {/* Decorative wave at bottom */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          className="w-full h-12 md:h-16"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0,0 C300,80 900,80 1200,0 L1200,120 L0,120 Z"
            fill="hsl(var(--sand-50))"
          />
        </svg>
      </div>
    </section>
  );
};
